import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Clock3, Loader2, PackageCheck, RefreshCw, Truck } from "lucide-react";

import { getOrderTracking } from "../../api/marketplace";
import { FeedbackMessage, getApiErrorMessage } from "../ui/Feedback";

const formatDate = (value) => value
  ? new Intl.DateTimeFormat("fr", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value))
  : "Non enregistré";

export default function OrderTracking() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getOrderTracking();
      setItems(response.data?.results || []);
    } catch (requestError) {
      setItems([]);
      setError(getApiErrorMessage(requestError, "Impossible de charger le suivi."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const metrics = useMemo(() => {
    const confirmed = items.filter((item) => item.confirmed_at).length;
    const delivered = items.filter((item) => item.delivery_status === "delivered").length;
    return [
      { label: "Commandes reçues", value: items.length, icon: Clock3 },
      { label: "Confirmées", value: confirmed, icon: CheckCircle2 },
      { label: "Livrées", value: delivered, icon: PackageCheck },
      { label: "Taux de livraison", value: confirmed ? `${Math.round((delivered / confirmed) * 100)} %` : "-", icon: Truck },
    ];
  }, [items]);

  return (
    <div className="page-shell px-1 py-2">
      <header className="page-header-card flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div><p className="eyebrow">Opérations</p><h1 className="mt-3 text-2xl font-semibold">Suivi des commandes</h1></div>
        <button type="button" className="btn-secondary" onClick={load} disabled={loading}><RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Actualiser</button>
      </header>
      {error ? <FeedbackMessage type="error" title="Suivi indisponible" message={error} onClose={() => setError("")} /> : null}
      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {metrics.map(({ label, value, icon: Icon }) => <div key={label} className="metric-card"><Icon size={17} className="tone-accent" /><p className="mt-4 text-2xl font-semibold">{value}</p><p className="mt-1 text-sm tone-muted">{label}</p></div>)}
      </section>
      <section className="surface-card p-0">
        {loading ? <div className="empty-state m-5 gap-2"><Loader2 size={18} className="animate-spin" /> Chargement du suivi...</div> : !items.length ? <div className="empty-state m-5">Le suivi apparaîtra dès la réception de la première commande.</div> : (
          <div className="overflow-x-auto"><table className="teqa-table"><thead><tr><th>Commande</th><th>Réception</th><th>Confirmation</th><th>Livraison</th><th>Commission</th></tr></thead><tbody>
            {items.map((item) => <tr key={item.order_id}><td>#{String(item.order_id).slice(0, 8)}</td><td>{formatDate(item.order_created_at)}</td><td>{formatDate(item.confirmed_at)}</td><td><span className={`badge ${item.delivery_status === "delivered" ? "badge--confirmed" : "badge--pending"}`}>{item.delivery_status}</span></td><td>{item.commission_id ? <span className="badge badge--active">Créée</span> : <span className="tone-muted">En attente</span>}</td></tr>)}
          </tbody></table></div>
        )}
      </section>
    </div>
  );
}
